import { lazy, useContext, useEffect, useRef, useState } from 'react';
import { AppContext } from '../../App';
import { initiateConsent } from '../../Services/PFMClient/ConsentService';
import { FlowType } from '../../Utils/constants';
import { captureException } from '../../Utils/sentryUtils';
import { EVENTCODES, pushEventToParent } from '../../Utils/eventUtils';
import {
  getAltJourney,
  getIsLoanJourney,
  getMahindraThemeMode,
  getSdkFlowSelection,
  getTenant,
  setReferenceId,
} from '../../Utils/storageUtils';
import { altJourneyType, TenantType } from '../../Utils/types';
import { ROUTES } from '../../Utils/routes';
import { useNavigate } from 'react-router-dom';
import AAWrapper from './AAWrapper';

const ConsentFailure = lazy(() => {
  return import('./ConsentFailure');
});

const FIPSelection = lazy(() => {
  return import('./FIPSelection');
});

export const enum AASTEPS {
  FIP_SELECTION,
  CONSENT_CREATION,
  AA_JOURNEY,
  AA_JOURNEY_COMPLETED,
  CONSENT_FAILURE,
}

const AA: React.FC<{
  isError: any;
}> = ({ isError }) => {
  const [isInfoAvailable, setIsInfoAvailable] = useState(true);

  const {
    navigateToDashboard,
    setIsLoading,
    currentFlowType,
    isAdditionalAccountJourney,
    isLoanJourney,
    AAInternalStep,
    setAAInternalStep,
    selectedFIP,
    setSelectedFIP,
    partialLoading,
    setPartialLoading,
    fipSelectionPage,
    currentMetaData,
    setCurrentRoute,
    statusData,
    setShowToastAA,
    setShowAUErrorSheet,
    setBackButtonHandler,
  }: any = useContext(AppContext);

  const [redirectionURL, setRedirectionURL] = useState('');
  const [journeyStarted, setJourneyStarted] = useState(false); // Track whether the journey has started
  const isPartial = useRef<boolean>(partialLoading);
  const navigate = useNavigate();

  const gotoRoot = () => {
    navigateToDashboard();
  };

  const goBackOnJourneyFailure = () => {
    if (currentFlowType === FlowType.BANKS) {
      if (!isAdditionalAccountJourney) {
        setTimeout(() => {
          setAAInternalStep(AASTEPS.AA_JOURNEY);
        }, 0);
        setIsInfoAvailable(true);
        handleFIPSelection([], ['PFM_BANKS', 'PFM_BANKS', 'PFM_BANKS']);
        setPartialLoading(true);
      } else {
        gotoRoot();
      }
    } else if (currentFlowType === FlowType.STOCKS) {
      gotoRoot();
    }
  };

  const createConsentForUser = async (
    fipIds: string[],
    templateType: string[]
  ) => {
    try {
      let meta = undefined;
      if (getTenant() === TenantType.JFS) {
        if (currentFlowType !== undefined && currentFlowType !== null) {
          meta = { fiType: FlowType[currentFlowType] };
        }
      }
      pushEventToParent({
        eventCode: 'CONSENT_CREATION_INITIATED',
        meta: meta,
        ...(isAdditionalAccountJourney
          ? {
              postHogEventName: `${FlowType[currentFlowType]}_CONSENT_CREATION_INITIATED`,
            }
          : {}),
      });

      const initiateConsentResponse: any = await initiateConsent({
        fipIds: fipIds,
        templateType: templateType,
      });

      if (initiateConsentResponse?.data === 'ERROR') {
        if (getTenant() === TenantType.JFS) {
          setShowToastAA(true);

          if (statusData.current.approvedConsentsAvailable) {
            setCurrentRoute(ROUTES.DASHBOARD);
          } else {
            setCurrentRoute(ROUTES.JFS_WELCOME);
            navigate('/welcome');
          }
        } else if (getTenant() === TenantType.AU) {
          setShowAUErrorSheet(true);
          setCurrentRoute(ROUTES.DASHBOARD);
        } else {
          isError();
        }

        try {
          let meta: any = { data: JSON.stringify(initiateConsentResponse) };
          if (getTenant() === TenantType.JFS) {
            if (currentFlowType !== undefined && currentFlowType !== null) {
              meta = { ...meta, fiType: FlowType[currentFlowType] };
            }
          }
          pushEventToParent({
            eventCode: 'CONSENT_CREATION_FAILED',
            meta: meta,
            ...(isAdditionalAccountJourney
              ? {
                  postHogEventName: `${FlowType[currentFlowType]}_CONSENT_CREATION_FAILED`,
                }
              : {}),
          });
        } catch (e) {
          captureException(e);
        }

        return;
      } else {
        let meta = undefined;
        if (getTenant() === TenantType.JFS) {
          if (currentFlowType !== undefined && currentFlowType !== null) {
            meta = { fiType: FlowType[currentFlowType] };
          }
        }
        pushEventToParent({
          eventCode: 'CONSENT_CREATION_SUCCESS',
          meta: meta,
          ...(isAdditionalAccountJourney
            ? {
                postHogEventName: `${FlowType[currentFlowType]}_CONSENT_CREATION_SUCCESS`,
              }
            : {}),
        });
      }
      setReferenceId(initiateConsentResponse.data.referenceId);

      if (getTenant() === TenantType.YESBANK) {
        if (getAltJourney() === altJourneyType.YESONLINE) {
          setRedirectionURL(
            `${initiateConsentResponse.data.redirectionUrl}?altFlow=YESONLINE`
          );
        } else {
          setRedirectionURL(`${initiateConsentResponse.data.redirectionUrl}`);
        }
      } else if (getTenant() === TenantType.AU) {
        setRedirectionURL(
          `${initiateConsentResponse.data.redirectionUrl}?isAubank=true`
        );
      } else if (getTenant() === TenantType.MAHINDRA) {
        const url = initiateConsentResponse.data.redirectionUrl;
        const isDarkMode = getMahindraThemeMode?.() === 'dark';
        const finalUrl = url.includes('?')
          ? `${url}&darkMode=${isDarkMode}`
          : `${url}?darkMode=${isDarkMode}`;
        setRedirectionURL(finalUrl);
      } else if (getIsLoanJourney()) {
        setRedirectionURL(`${initiateConsentResponse.data.redirectionUrl}`);
      } else if (getTenant() === TenantType.PB && !getIsLoanJourney()) {
        setRedirectionURL(
          `${initiateConsentResponse.data.redirectionUrl}?customLogo=PBLOGO`
        );
      } else {
        setRedirectionURL(`${initiateConsentResponse.data.redirectionUrl}`);
      }
      setAAInternalStep(AASTEPS.AA_JOURNEY);
    } catch (e: any) {
      captureException(e);
      let meta = undefined;
      if (getTenant() === TenantType.JFS) {
        if (currentFlowType !== undefined && currentFlowType !== null) {
          meta = { fiType: FlowType[currentFlowType] };
        }
      }
      pushEventToParent({
        eventCode: 'CONSENT_CREATION_FAILED',
        meta: meta,
        ...(isAdditionalAccountJourney
          ? {
              postHogEventName: `${FlowType[currentFlowType]}_CONSENT_CREATION_FAILED`,
            }
          : {}),
      });
    }
  };

  const handleFIPSelection = (fipIds: string[], template: string[]) => {
    if (fipIds.length > 0) {
      pushEventToParent({
        eventCode: 'FIP_SELECTED',
        meta: {
          fipId: fipIds,
          fipName: fipIds,
        },
      });
    }
    setSelectedFIP(fipIds);
    createConsentForUser(fipIds, template);
    setJourneyStarted(true);
  };

  useEffect(() => {
    if (
      (getTenant() === TenantType.YESBANK ||
        getTenant() === TenantType.MAHINDRA) &&
      !journeyStarted
    ) {
      if (selectedFIP.length > 0) {
        handleFIPSelection(selectedFIP, ['PFM_RD_FD']);
      }
    }
  }, [journeyStarted, selectedFIP]);

  useEffect(() => {
    window.onpopstate = () => {
      if (isAdditionalAccountJourney) {
        setIsLoading(false);
        gotoRoot();
      } else {
        setIsLoading(false);
        pushEventToParent({
          eventCode: EVENTCODES.BACK_BUTTON_PFMDB,
        });
        pushEventToParent({
          eventCode: 'BACK_BUTTON_PRESSED',
        });
      }
    };
    return () => {
      window.onpopstate = () => {};
    };
  }, [isAdditionalAccountJourney]);

  useEffect(() => {
    isPartial.current = partialLoading;
  }, [partialLoading]);

  useEffect(() => {
    if (getTenant() === TenantType.PB) {
      window.onpopstate = () => {
        // Push the user forward again to prevent back
        window.history.pushState(null, '', window.location.href);
      };
    }
  }, []);

  useEffect(() => {
    if (getTenant() === TenantType.MAHINDRA && selectedFIP.length <= 0) {
      setPartialLoading(true);
      handleFIPSelection([], ['PFM_BANKS']);
    } else if (getTenant() === TenantType.YESBANK && selectedFIP.length <= 0) {
      if (currentFlowType === FlowType.MUTUAL_FUNDS) {
        setPartialLoading(true);
        if (process.env.REACT_APP_IS_MOCK !== 'true') {
          handleFIPSelection(
            ['CAMSRTAFIP', 'CDSLFIP', 'kfinmf-fip'],
            ['PFM_MF', 'PFM_MF', 'PFM_MF']
          );
        } else {
          handleFIPSelection(['FIP-ID'], ['PFM_MF', 'PFM_MF', 'PFM_MF']);
        }
      } else {
        setPartialLoading(true);
        handleFIPSelection(
          [],
          ['PFM_RD_FD', 'PFM_RD_FD', 'PFM_RD_FD', 'PFM_RD_FD']
        );
      }
    } else if (getTenant() === TenantType.PB) {
      if (currentFlowType === FlowType.BANKS) {
        if (
          isAdditionalAccountJourney ||
          (!isAdditionalAccountJourney && !isLoanJourney.current)
        ) {
          setPartialLoading(true);
        }

        if (!isAdditionalAccountJourney && !isLoanJourney.current) {
          if (getSdkFlowSelection()) {
            setAAInternalStep(AASTEPS.FIP_SELECTION);
          } else {
            handleFIPSelection([], ['PFM_BANKS', 'PFM_BANKS', 'PFM_BANKS']);
          }
        } else {
          setAAInternalStep(AASTEPS.FIP_SELECTION);
        }
      } else if (currentFlowType === FlowType.STOCKS) {
        setIsLoading(true);
        if (process.env.REACT_APP_IS_MOCK !== 'true') {
          createConsentForUser(
            ['CDSLFIP', 'fip@nsdl'],
            ['PFM_STOCKS', 'PFM_STOCKS']
          );
        } else {
          createConsentForUser(['FIP-ID'], ['PFM_STOCKS', 'PFM_STOCKS']);
        }
      }
    } else if (getTenant() === TenantType.JFS) {
      if (AAInternalStep === AASTEPS.AA_JOURNEY) {
        setPartialLoading(true);
        if (currentFlowType === FlowType.STOCKS) {
          if (process.env.REACT_APP_IS_MOCK !== 'true') {
            handleFIPSelection(
              ['fip@nsdl', 'CDSLFIP'],
              ['PFM_STOCKS', 'PFM_STOCKS', 'PFM_STOCKS', 'PFM_STOCKS']
            );
          } else {
            handleFIPSelection(
              [],
              ['PFM_STOCKS', 'PFM_STOCKS', 'PFM_STOCKS', 'PFM_STOCKS']
            );
          }
        } else if (currentFlowType === FlowType.MUTUAL_FUNDS) {
          if (process.env.REACT_APP_IS_MOCK !== 'true') {
            handleFIPSelection(
              ['CAMSRTAFIP', 'CDSLFIP', 'kfinmf-fip', 'fip@nsdl'],
              ['PFM_MF', 'PFM_MF', 'PFM_MF', 'PFM_MF']
            );
          } else {
            handleFIPSelection([], ['PFM_MF', 'PFM_MF', 'PFM_MF', 'PFM_MF']);
          }
        } else if (currentFlowType === FlowType.BANKS) {
          handleFIPSelection(
            [],
            ['PFM_BANKS', 'PFM_BANKS', 'PFM_BANKS', 'PFM_BANKS', 'PFM_BANKS']
          );
        }
      } else {
        setPartialLoading(true);
        setAAInternalStep(AASTEPS.FIP_SELECTION);
      }
    } else if (getTenant() === TenantType.AU) {
      if (AAInternalStep === AASTEPS.AA_JOURNEY) {
        setPartialLoading(true);
        if (currentFlowType === FlowType.STOCKS) {
          if (process.env.REACT_APP_IS_MOCK !== 'true') {
            handleFIPSelection(
              ['fip@nsdl', 'CDSLFIP'],
              ['PFM_STOCKS', 'PFM_STOCKS', 'PFM_STOCKS', 'PFM_STOCKS']
            );
          } else {
            handleFIPSelection(
              [],
              ['PFM_STOCKS', 'PFM_STOCKS', 'PFM_STOCKS', 'PFM_STOCKS']
            );
          }
        } else if (currentFlowType === FlowType.MUTUAL_FUNDS) {
          if (process.env.REACT_APP_IS_MOCK !== 'true') {
            handleFIPSelection(
              ['CAMSRTAFIP', 'CDSLFIP', 'kfinmf-fip', 'fip@nsdl'],
              ['PFM_MF', 'PFM_MF', 'PFM_MF']
            );
          } else {
            handleFIPSelection([], ['PFM_MF', 'PFM_MF', 'PFM_MF']);
          }
        } else if (currentFlowType === FlowType.BANKS) {
          handleFIPSelection(
            [],
            ['PFM_BANKS', 'PFM_BANKS', 'PFM_BANKS', 'PFM_BANKS', 'PFM_BANKS']
          );
        } else if (currentFlowType === FlowType.TERM_DEPOSITS) {
          handleFIPSelection([], ['PFM_FD', 'PFM_FD', 'PFM_FD', 'PFM_FD']);
        }
      }
    }
  }, []);

  const renderComponents = () => {
    switch (AAInternalStep) {
      case AASTEPS.FIP_SELECTION:
        return (
          <FIPSelection
            onSelect={handleFIPSelection}
            isAdditionalAccountJourney={isAdditionalAccountJourney}
            gotoRoot={gotoRoot}
          />
        );
      case AASTEPS.CONSENT_CREATION:
        return <></>;
      case AASTEPS.AA_JOURNEY:
        return (
          <AAWrapper
            redirectionURL={redirectionURL}
            isInfoAvailable={isInfoAvailable}
            setIsInfoAvailable={setIsInfoAvailable}
            gotoRoot={gotoRoot}
            goBackOnJourneyFailure={goBackOnJourneyFailure}
            setAAInternalStep={setAAInternalStep}
            isPartial={isPartial}
            partialLoading={partialLoading}
            setPartialLoading={setPartialLoading}
          />
        );
      case AASTEPS.AA_JOURNEY_COMPLETED:
        return <></>;
      case AASTEPS.CONSENT_FAILURE:
        return (
          <ConsentFailure goBackOnJourneyFailure={goBackOnJourneyFailure} />
        );
      default:
        return <></>;
    }
  };

  return <>{renderComponents()}</>;
};

export default AA;
